import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, Observable, catchError, filter, switchMap, take, throwError } from 'rxjs';
import { AuthService } from '../auth/auth.service';
import { AppRoutes } from 'src/app/shared/routes/routes';

@Injectable()
export class AuthRefreshInterceptor implements HttpInterceptor {
  private static readonly RETRY_HEADER = 'X-Auth-Refresh-Retry';

  private authService = inject(AuthService);
  private router = inject(Router);

  private isRefreshing = false;
  private refreshTokenSubject = new BehaviorSubject<string | null | undefined>(null);

  intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    return next.handle(request).pipe(
      catchError((error: HttpErrorResponse) => {
        if (error.status !== 401 || !this.shouldHandle401(request)) {
          return throwError(() => error);
        }

        return this.handle401Error(request, next, error);
      })
    );
  }

  private shouldHandle401(request: HttpRequest<unknown>): boolean {
    if (request.headers.has(AuthRefreshInterceptor.RETRY_HEADER)) {
      return false;
    }

    return !request.url.includes('refresh')
      && !request.url.includes('login')
      && !request.url.includes('logout')
      && !request.url.includes('two-factor')
      && !request.url.includes('select-location');
  }

  private handle401Error(
    request: HttpRequest<unknown>,
    next: HttpHandler,
    originalError: HttpErrorResponse
  ): Observable<HttpEvent<unknown>> {
    const refreshToken = this.authService.getRefreshTokenFromStorage();

    if (!refreshToken) {
      this.handleLogout();
      return throwError(() => originalError);
    }

    if (!this.isRefreshing) {
      this.isRefreshing = true;
      this.refreshTokenSubject.next(undefined);

      return this.authService.refreshToken().pipe(
        switchMap((success: boolean) => {
          this.isRefreshing = false;

          if (!success) {
            this.refreshTokenSubject.next(null);
            this.handleLogout();
            return throwError(() => originalError);
          }

          const authToken = this.authService.getTokenFromStorage();
          const refreshToken = this.authService.getRefreshTokenFromStorage();
          this.refreshTokenSubject.next(authToken);

          return next.handle(this.withRetryHeader(request, authToken, refreshToken));
        }),
        catchError((error) => {
          this.isRefreshing = false;
          this.refreshTokenSubject.next(null);
          this.handleLogout();
          return throwError(() => error);
        })
      );
    }

    return this.refreshTokenSubject.pipe(
      filter((token): token is string | null => token !== undefined),
      take(1),
      switchMap((token) => {
        if (!token) {
          this.handleLogout();
          return throwError(() => originalError);
        }

        return next.handle(
          this.withRetryHeader(
            request,
            token,
            this.authService.getRefreshTokenFromStorage(),
          ),
        );
      })
    );
  }

  private withRetryHeader(
    request: HttpRequest<unknown>,
    authToken: string,
    refreshToken: string,
  ): HttpRequest<unknown> {
    return request.clone({
      setHeaders: {
        Authorization: `Bearer ${authToken}`,
        'X-Refresh-Token': refreshToken,
        [AuthRefreshInterceptor.RETRY_HEADER]: 'true',
      },
    });
  }

  private handleLogout(): void {
    this.authService.logout(false, true).subscribe();
    this.router.navigate([AppRoutes.auth.login]);
  }
}
